import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PublicLayout } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { MapPin, Phone, MessageCircle, Star, Share2, Heart, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { ProfessionalReviews, ProfessionalScheduler } from "@/pages/Onda2";

export default function PerfilProfissional() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({name:"",phone:"",email:"",service:"",city:"",notes:"",preferred_contact:"whatsapp",consent:false});
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(()=>{ api.get(`/professionals/${id}`).then(r=>setP(r.data)).catch(()=>{}); }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("É preciso autorizar o compartilhamento dos dados."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/leads", {...form, professional_id: id});
      setSubmitted(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (!p) return <PublicLayout><div className="max-w-4xl mx-auto p-10">Carregando...</div></PublicLayout>;

  return (
    <PublicLayout>
      <div className="bg-gradient-to-b from-[#E6F4F1] to-transparent">
        <div className="max-w-6xl mx-auto px-6 py-10 grid md:grid-cols-[220px,1fr] gap-8">
          {p.photo_url && <img src={p.photo_url} alt={p.name} className="w-52 h-52 rounded-3xl object-cover gs-shadow-lg"/>}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-[#0D7C66] text-white border-0"><ShieldCheck className="w-3 h-3 mr-1"/>Perfil validado</Badge>
              {p.featured && <Badge className="bg-[#FF7D54] text-white border-0">Destaque</Badge>}
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold" style={{fontFamily:"Plus Jakarta Sans"}}>{p.name}</h1>
            <div className="text-lg text-slate-600 mt-1">{p.profession} • {p.specialty}</div>
            {p.register_number && <div className="text-sm text-slate-500 mt-1">{p.register_number}</div>}
            <div className="mt-2 text-sm text-slate-600 flex items-center gap-1"><MapPin className="w-4 h-4"/>{p.city}{p.neighborhood?` • ${p.neighborhood}`:""}</div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="tenho-interesse-btn" className="bg-[#FF7D54] hover:bg-[#e56a44] h-11 px-6">Tenho Interesse</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  {submitted ? (
                    <div className="py-2">
                      <div className="w-12 h-12 rounded-full bg-[#E6F4F1] flex items-center justify-center mb-3"><CheckCircle2 className="w-6 h-6 text-[#0D7C66]"/></div>
                      <DialogHeader><DialogTitle>Solicitação enviada!</DialogTitle></DialogHeader>
                      <p className="text-sm text-slate-600 mt-2">Encaminhamos seus dados ao profissional. Você pode acompanhar o status pela sua área.</p>
                      <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm space-y-1">
                        <div><span className="text-slate-500">Nº protocolo:</span> <b>{submitted.number}</b></div>
                        <div><span className="text-slate-500">Profissional:</span> {p.name}</div>
                        <div><span className="text-slate-500">Status inicial:</span> <b className="text-[#0D7C66]">AGUARDANDO CONTATO</b></div>
                      </div>
                      <DialogFooter className="mt-4"><Button data-testid="lead-close-btn" onClick={()=>{setOpen(false); setSubmitted(null);}}>Fechar</Button><Button data-testid="lead-track-btn" onClick={()=>window.location.href=`/pedidos/${submitted.number}`} className="bg-[#0D7C66] hover:bg-[#074E41]">Acompanhar guia</Button></DialogFooter>
                    </div>
                  ) : (
                    <form onSubmit={submit} className="space-y-3">
                      <DialogHeader><DialogTitle>Solicitar contato com {p.name}</DialogTitle></DialogHeader>
                      <p className="text-xs text-slate-500">Informe os dados abaixo. Não solicitamos histórico clínico.</p>
                      <Input data-testid="lead-name" placeholder="Seu nome" required value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/>
                      <div className="grid grid-cols-2 gap-2">
                        <Input data-testid="lead-phone" placeholder="Telefone/WhatsApp" required value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})}/>
                        <Input data-testid="lead-email" type="email" placeholder="E-mail" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
                      </div>
                      <Input data-testid="lead-service" placeholder="Serviço de interesse (opcional)" value={form.service} onChange={e=>setForm({...form,service:e.target.value})}/>
                      <Input data-testid="lead-city" placeholder="Sua cidade" value={form.city} onChange={e=>setForm({...form,city:e.target.value})}/>
                      <select data-testid="lead-contact-pref" value={form.preferred_contact} onChange={e=>setForm({...form,preferred_contact:e.target.value})} className="w-full h-10 rounded-md border border-input px-3 text-sm">
                        <option value="whatsapp">Prefiro contato por WhatsApp</option>
                        <option value="phone">Prefiro ligação</option>
                        <option value="email">Prefiro e-mail</option>
                      </select>
                      <Textarea data-testid="lead-notes" placeholder="Observação (opcional)" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/>
                      <label className="flex gap-2 items-start text-xs text-slate-600">
                        <Checkbox data-testid="lead-consent" checked={form.consent} onCheckedChange={v=>setForm({...form,consent:!!v})}/>
                        <span>Autorizo o compartilhamento dos dados desta solicitação com o profissional selecionado para que ele possa entrar em contato comigo.</span>
                      </label>
                      <DialogFooter>
                        <Button data-testid="lead-submit" type="submit" disabled={loading} className="bg-[#0D7C66] hover:bg-[#074E41]">{loading?"Enviando...":"Enviar solicitação"}</Button>
                      </DialogFooter>
                    </form>
                  )}
                </DialogContent>
              </Dialog>
              {p.whatsapp && <Button variant="outline" data-testid="whatsapp-btn"><MessageCircle className="w-4 h-4 mr-1"/>WhatsApp</Button>}
              <Button variant="outline" data-testid="favorite-btn"><Heart className="w-4 h-4 mr-1"/>Favoritar</Button>
              <Button variant="outline" data-testid="share-btn" onClick={()=>{navigator.clipboard.writeText(window.location.href); toast.success("Link copiado");}}><Share2 className="w-4 h-4 mr-1"/>Compartilhar</Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 grid md:grid-cols-[1fr,320px] gap-10">
        <div>
          <h2 className="text-xl font-bold mb-3" style={{fontFamily:"Plus Jakarta Sans"}}>Sobre</h2>
          <p className="text-slate-700 leading-relaxed">{p.description || "Profissional cadastrado na rede GIRO+SAÚDE."}</p>

          {p.services?.length > 0 && (
            <>
              <h2 className="text-xl font-bold mt-8 mb-3" style={{fontFamily:"Plus Jakarta Sans"}}>Serviços</h2>
              <div className="flex flex-wrap gap-2">{p.services.map(s=><Badge key={s} variant="outline" className="text-sm">{s}</Badge>)}</div>
            </>
          )}

          <ProfessionalScheduler professionalId={p.id} professionalName={p.name}/>
          <ProfessionalReviews professionalId={p.id}/>
        </div>
        <aside className="space-y-4">
          {p.benefit_summary && (
            <div className="rounded-2xl bg-[#FEF8F3] p-5 border border-[#FF7D54]/20">
              <div className="text-xs font-semibold uppercase tracking-widest text-[#FF7D54] mb-1"><Star className="w-3 h-3 inline mr-1"/>Benefício associado</div>
              <div className="font-semibold text-slate-900">{p.benefit_summary}</div>
              <div className="text-xs text-slate-500 mt-1">Utilize sua carteira digital no atendimento.</div>
            </div>
          )}
          <div className="rounded-2xl bg-white border border-slate-200 p-5 gs-shadow">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Contato</div>
            {p.phone && <div className="text-sm flex items-center gap-2 mb-2"><Phone className="w-4 h-4"/>{p.phone}</div>}
            {p.whatsapp && <div className="text-sm flex items-center gap-2 mb-2"><MessageCircle className="w-4 h-4"/>{p.whatsapp}</div>}
            {p.address && <div className="text-sm flex items-center gap-2"><MapPin className="w-4 h-4"/>{p.address}</div>}
          </div>
        </aside>
      </div>
    </PublicLayout>
  );
}
